/**
 * Layer registry for the World View.
 *
 * Organized by category so the sidebar groups them logically.
 * Each layer has an id, icon, label, default-on, and a color/kind hint.
 *
 * Inspired by worldmonitor's layer registry (AGPL-3.0); data + selection
 * tailored to SupplyLens' logistics focus.
 */

export type LayerKind = "incident" | "flight" | "ship" | "fleet" | "future"

export interface LayerDef {
  id: string
  icon: string
  label: string
  category: string
  /**
   * Backend-layer kind this entry controls.
   * - one of the `LayerKind` strings → fetched from `/api/realtime/globe?layers=…`
   * - `"future"` → locked / coming-soon, toggle disabled
   * - `null` → client-only layer (rendered purely on the front-end, no backend call)
   */
  kind: LayerKind | null
  color: string
  defaultOn: boolean
  /** Search synonyms for the sidebar search. */
  synonyms?: string[]
  /** Short blurb shown on hover or in help. */
  note?: string
  locked?: boolean
}

export const LAYER_CATEGORIES = [
  "Geography",
  "Your fleet",
  "Traffic",
  "Disruption",
  "Freight",
  "Infrastructure",
  "Intel",
] as const

export type LayerCategory = (typeof LAYER_CATEGORIES)[number]

export const LAYERS: LayerDef[] = [
  // Geography — client-only polygon overlays
  { id: "countries", icon: "▣", label: "Countries", category: "Geography",
    kind: null, color: "#64748b", defaultOn: false,
    synonyms: ["borders", "regions", "iso", "nations", "polygons"],
    note: "Click any country to open the region deep-dive." },

  // Your fleet
  { id: "fleet", icon: "★", label: "My fleet", category: "Your fleet",
    kind: "fleet", color: "#facc15", defaultOn: true,
    synonyms: ["owned", "mine", "adopted"], note: "Assets adopted or directly integrated into your tenant." },
  { id: "fleet-arcs", icon: "⌒", label: "Planned routes", category: "Your fleet",
    kind: "future", color: "#facc15", defaultOn: false,
    synonyms: ["trade routes", "arcs", "lanes"], note: "Great-circle arcs between each shipment's origin and destination." },

  // Traffic
  { id: "flight", icon: "✈", label: "Flights (live)", category: "Traffic",
    kind: "flight", color: "#a5f3fc", defaultOn: true,
    synonyms: ["aircraft", "opensky", "aviation"], note: "Live from OpenSky (no key); altitude-coloured." },
  { id: "ship", icon: "⛴", label: "Ships", category: "Traffic",
    kind: "ship", color: "#67e8f9", defaultOn: true,
    synonyms: ["vessels", "ais", "maritime"], note: "Synthetic along real corridors; AISStream integration planned." },

  // Disruption
  { id: "incident", icon: "⚠", label: "Disruptions", category: "Disruption",
    kind: "incident", color: "#ef4444", defaultOn: true,
    synonyms: ["incidents", "news", "disruption", "risk"], note: "AI-synthesized incidents from news + weather + port feeds." },
  { id: "chokepoints", icon: "⚓", label: "Chokepoints", category: "Disruption",
    kind: "future", color: "#f97316", defaultOn: false,
    synonyms: ["suez", "panama", "hormuz", "malacca", "bab-el-mandeb"], note: "Strategic waterways + current status." },
  { id: "weather", icon: "⛈", label: "Weather alerts", category: "Disruption",
    kind: "future", color: "#38bdf8", defaultOn: false,
    synonyms: ["storms", "cyclone", "hurricane"], note: "NOAA + global severe weather — coming in Phase 7." },

  // Freight
  { id: "rate-spikes", icon: "📈", label: "Rate spikes", category: "Freight",
    kind: "future", color: "#f59e0b", defaultOn: false,
    synonyms: ["freight", "bdi", "fbx", "rates"], note: "Lane-level rate change overlays — ties to freight-market module." },
  { id: "commodity-ports", icon: "⛵", label: "Commodity ports", category: "Freight",
    kind: "future", color: "#fbbf24", defaultOn: false,
    synonyms: ["oil", "gas", "grain", "ports"] },
  { id: "trade-routes", icon: "⇌", label: "Trade routes", category: "Freight",
    kind: "future", color: "#22d3ee", defaultOn: false,
    synonyms: ["lanes", "shipping lanes"] },

  // Infrastructure
  { id: "warehouses", icon: "🏭", label: "Warehouses", category: "Infrastructure",
    kind: "future", color: "#84cc16", defaultOn: false,
    synonyms: ["dc", "distribution center"] },
  { id: "pipelines", icon: "⫽", label: "Pipelines", category: "Infrastructure",
    kind: "future", color: "#fb923c", defaultOn: false },
  { id: "cables", icon: "⋯", label: "Undersea cables", category: "Infrastructure",
    kind: "future", color: "#67e8f9", defaultOn: false,
    synonyms: ["fiber", "internet"] },
  { id: "datacenters", icon: "▦", label: "AI data centers", category: "Infrastructure",
    kind: "future", color: "#a78bfa", defaultOn: false,
    synonyms: ["cloud", "compute"] },

  // Intel
  { id: "conflicts", icon: "⚔", label: "Armed conflicts", category: "Intel",
    kind: "future", color: "#ef4444", defaultOn: false,
    synonyms: ["war", "military", "acled"] },
  { id: "protests", icon: "✊", label: "Protests", category: "Intel",
    kind: "future", color: "#f97316", defaultOn: false,
    synonyms: ["strikes", "labour"] },
  { id: "sanctions", icon: "🚫", label: "Sanctions", category: "Intel",
    kind: "future", color: "#e11d48", defaultOn: false,
    synonyms: ["embargo", "export control"] },
  { id: "cyber", icon: "⎈", label: "Cyber threats", category: "Intel",
    kind: "future", color: "#c084fc", defaultOn: false,
    synonyms: ["ddos", "ransomware"] },
]

export function layerMatchesSearch(l: LayerDef, q: string): boolean {
  if (!q) return true
  const needle = q.trim().toLowerCase()
  if (!needle) return true
  if (l.label.toLowerCase().includes(needle)) return true
  if (l.id.toLowerCase().includes(needle)) return true
  if (l.category.toLowerCase().includes(needle)) return true
  if (l.synonyms?.some((s) => s.toLowerCase().includes(needle))) return true
  return false
}

export function layersByCategory(defs: LayerDef[]): Record<LayerCategory, LayerDef[]> {
  const out = Object.fromEntries(LAYER_CATEGORIES.map((c) => [c, [] as LayerDef[]])) as Record<
    LayerCategory,
    LayerDef[]
  >
  for (const l of defs) {
    const bucket = out[l.category as LayerCategory]
    if (bucket) bucket.push(l)
  }
  return out
}

export function defaultLayerState(): Record<string, boolean> {
  return Object.fromEntries(LAYERS.map((l) => [l.id, l.defaultOn]))
}
